import { useState } from "react";
import { I } from "@/components/Icon";
import type { DashboardView } from "@/lib/types";

export function ViewPicker({
  views,
  activeId,
  currentUserId,
  isAdmin,
  onPick,
  onToggleDefault,
  onDelete,
}: {
  views: DashboardView[];
  activeId: number | null;
  currentUserId: number | null;
  isAdmin: boolean;
  onPick: (v: DashboardView) => void;
  onToggleDefault: (v: DashboardView) => void;
  onDelete: (v: DashboardView) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = views.find((v) => v.id === activeId) ?? null;
  const mainText = active?.name ?? "None selected";
  const subText = active
    ? active.visibility === "shared"
      ? `Shared · ${views.length} total`
      : `Private · ${views.length} total`
    : `${views.length} available`;

  return (
    <div style={{ position: "relative", display: "inline-block", minWidth: 240 }}>
      {/* Trigger visually matches the sibling App / Period pickers —
          small uppercase label, main name, sub-line. Makes it obvious
          which control this is without a separate caption outside. */}
      <button
        className="app-picker"
        onClick={() => setOpen((o) => !o)}
        data-open={open}
        style={{ width: "100%", minWidth: 0, margin: 0 }}
        title="Saved dashboard views"
      >
        <span className="app-icon" aria-hidden>
          ★
        </span>
        <span className="app-meta" style={{ minWidth: 0 }}>
          <span
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "var(--c-text-4)",
              display: "block",
              lineHeight: 1.1,
            }}
          >
            Saved dashboards
          </span>
          <span className="app-name">{mainText}</span>
          <span className="app-sub">{subText}</span>
        </span>
        <span className="app-chev">{I.chevronDown}</span>
      </button>
      {open && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
            }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              width: 320,
              background: "var(--c-surface)",
              border: "1px solid var(--c-border)",
              borderRadius: 8,
              boxShadow: "var(--sh-3)",
              padding: 4,
              zIndex: 50,
            }}
          >
            {views.map((v) => {
              const isOwner = v.owner_user_id === currentUserId;
              // Star visible to anyone; clicking it requires admin
              // for shared views, enforced server-side. We pre-check
              // and disable the click for non-admin on shared.
              const canToggleDefault = v.visibility === "private" ? isOwner : isAdmin;
              const canDelete = isOwner || (v.visibility === "shared" && isAdmin);
              const isActive = v.id === activeId;
              return (
                <div
                  key={v.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 8px",
                    borderRadius: 6,
                    background: isActive ? "var(--c-accent-50)" : "transparent",
                    color: isActive ? "var(--c-accent-700)" : "var(--c-text)",
                  }}
                >
                  <button
                    onClick={() => {
                      onPick(v);
                      setOpen(false);
                    }}
                    style={{
                      flex: 1,
                      textAlign: "left",
                      background: "transparent",
                      border: 0,
                      cursor: "pointer",
                      color: "inherit",
                      padding: 0,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {v.name}
                    </div>
                    <div
                      className="muted"
                      style={{ fontSize: 11, marginTop: 1 }}
                    >
                      {v.visibility === "shared"
                        ? `Shared · by ${v.owner_name ?? "—"}`
                        : "Private"}
                      {v.is_default && " · default"}
                    </div>
                  </button>
                  <button
                    onClick={() => canToggleDefault && onToggleDefault(v)}
                    disabled={!canToggleDefault}
                    title={
                      v.is_default
                        ? "Remove as default"
                        : canToggleDefault
                          ? "Set as default"
                          : v.visibility === "shared"
                            ? "Only an admin can set shared default"
                            : "You can only star your own private views"
                    }
                    style={{
                      border: 0,
                      background: "transparent",
                      cursor: canToggleDefault ? "pointer" : "not-allowed",
                      color: v.is_default
                        ? "var(--c-warn-500)"
                        : "var(--c-text-4)",
                      opacity: canToggleDefault ? 1 : 0.4,
                      fontSize: 16,
                      padding: "0 4px",
                      lineHeight: 1,
                    }}
                  >
                    {v.is_default ? "★" : "☆"}
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => onDelete(v)}
                      title="Delete view"
                      style={{
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--c-text-4)",
                        fontSize: 14,
                        padding: "0 4px",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
